/**
 * Links a visitor saved in VR ("Open after VR" on a popup's link or web page, an HTML mesh, a
 * scene-menu link): a headset session can't open a page, so once it ends the viewer lists them in
 * a small card over the page, each opening in a new tab.
 *
 * Built with DOM APIs only (no innerHTML of authored text); only http(s) links are listed.
 */
import type { VrSavedLink } from './vrMenus';

const CARD_CLASS = 'storysplat-vr-saved-links';

/** Only web links can be opened from the list. */
export function openableLink(url: string): boolean {
    try {
        const parsed = new URL(url, window.location.href);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

/** Show the card (replacing an earlier one); returns its removal. */
export function showVrSavedLinks(container: HTMLElement, links: readonly VrSavedLink[], words: { title: string; close: string }, accent: string): () => void {
    container.querySelector(`.${CARD_CLASS}`)?.remove();
    const usable = links.filter((l) => openableLink(l.url));
    if (!usable.length) return () => undefined;
    const card = document.createElement('div');
    card.className = CARD_CLASS;
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-label', words.title);
    card.style.cssText = 'position:absolute;left:50%;bottom:24px;transform:translateX(-50%);z-index:10050;width:min(420px,calc(100% - 32px));max-height:60%;overflow:auto;box-sizing:border-box;padding:14px 16px;border-radius:12px;background:rgba(18,18,20,0.94);color:#f2f0ed;font:14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;box-shadow:0 8px 28px rgba(0,0,0,0.4);';
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:8px;';
    const title = document.createElement('strong');
    title.textContent = words.title;
    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = '×';
    close.setAttribute('aria-label', words.close);
    close.style.cssText = 'background:none;border:0;color:inherit;font-size:22px;line-height:1;cursor:pointer;padding:2px 6px;';
    header.append(title, close);
    const list = document.createElement('ul');
    list.style.cssText = 'list-style:none;margin:0;padding:0;display:grid;gap:6px;';
    for (const link of usable) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = link.url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.textContent = link.label || link.url;
        a.style.cssText = `color:${accent};text-decoration:underline;overflow-wrap:anywhere;`;
        li.appendChild(a);
        list.appendChild(li);
    }
    card.append(header, list);
    // Clicks on the card stay off the canvas behind it.
    card.addEventListener('pointerdown', (e) => e.stopPropagation());
    const remove = () => card.remove();
    close.addEventListener('click', remove);
    container.appendChild(card);
    return remove;
}
